import {
  TextLinkWidget, Tooltip, TooltipPosition
  } from '@tableau/widgets';
import React from 'react';
import _ from 'underscore';

import { Messages } from '~src/codegen/Localize';
import { FormikCheckbox } from '~src/commonInfra/components/FormikCheckbox';
import { FormikTextBox } from '~src/commonInfra/components/FormikField';
import { HelpService } from '~src/commonInfra/services/HelpService';
import { getSessionManagementServiceInstance } from '~src/sessionManagement/SessionManagementService';
import { useFormikContext } from '~src/settings/FormikWrapperForStubbing';
import { componentRegistryEntry } from '~src/settings/general-settings/components/SettingsComponentRegistryFunctions';
import { usePositiveIntegerValidator } from '~src/settings/general-settings/utils';
import { learnMoreStyle, SettingsStyles } from '~src/settings/styles/CommonStyles';

import { SeverityLevelDropdown } from '../components/SeverityLevelDropdown';
import { MobileServerSettingsSubFormikProps } from '../server-settings/MobileServerSettingsCoordinator';
import { sectionDescription, sectionHeader, settingSection } from '../styles/MobileSecuritySectionStyle';
import { getSeverityFieldsForPolicy, getValuesForPolicy } from '../utils/MobileSecuritySettingsUtils';

export const MOBILE_SECURITY_SETTINGS_SECTION_TEST_ID = 'mobile-security-settings-section';
export const MOBILE_SECURITY_SETTINGS_SECTION_DESCRIPTION_TEST_ID = 'mobile-security-settings-section-description';
export const MOBILE_SECURITY_POLICIES_LEARN_MORE_LINK_TEST_ID = 'allow-mobile-security-policies-learn-more-link';
export const MOBILE_SECURITY_POLICIES_JAIL_BROKEN_DETECTION_CHECKBOX_TEST_ID = 'allow-mobile-security-policies-jail-broken-detection-checkbox';
export const MOBILE_SECURITY_POLICIES_JAIL_BROKEN_DETECTION_DROPDOWN_TEST_ID = 'allow-mobile-security-policies-jail-broken-detection-dropdown';
export const MOBILE_SECURITY_POLICIES_BLOCK_SCREENSHOT_CHECKBOX_TEST_ID = 'allow-mobile-security-policies-screenshot-checkbox';
export const MOBILE_SECURITY_POLICIES_ANTI_DEBUG_CHECKBOX_TEST_ID = 'allow-mobile-security-policies-anti-debug-checkbox';
export const MOBILE_SECURITY_POLICIES_MAX_OFFLINE_CHECKBOX_TEST_ID = 'allow-mobile-security-policies-max-offline-checkbox';
export const MOBILE_SECURITY_POLICIES_MAX_OFFLINE_DROPDOWN_TEST_ID = 'allow-mobile-security-policies-max-offline-dropdown';
export const MOBILE_SECURITY_POLICIES_MAX_OFFLINE_TEXT_FIELD_TEST_ID = 'allow-mobile-security-policies-max-offline-text-field';

const MOBILE_SECURITY_POLICIES_JAIL_BROKEN_DETECTION_ENABLED_FIELD = 'settings.mobileSecuritySettings.jailbroken_device.enabled';
const MOBILE_SECURITY_POLICIES_BLOCK_SCREENSHOT_ENABLED_FIELD = 'settings.mobileSecuritySettings.screenshot.enabled';
const MOBILE_SECURITY_POLICIES_MAX_OFFLINE_ENABLED_FIELD = 'settings.mobileSecuritySettings.max_offline.enabled';

const JAIL_BROKEN_DETECTION = 'jailbroken_device';
const MAX_OFFLINE = 'max_offline';

// We're setting identical iOS and Android config values but using iOS to represent the input value
const MAX_OFFLINE_VALUE = 'settings.mobileSecuritySettings.max_offline.iosConfig.value';

export const MAX_OFFLINE_DEBOUNCE_MS = 1000;

export function MobileSecuritySettingsSection(_props: MobileServerSettingsSubFormikProps) {
  const formik = useFormikContext<MobileServerSettingsSubFormikProps>();
  const sessionManagementService = getSessionManagementServiceInstance();

  // Don't show the error right away until after the user finishes typing
  // This code was noncompliant before we enabled the lint rule. If you're here, please consider fixing it! See TFS 1257824
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const setDebouncedFieldTouched = React.useCallback(_.debounce((policy: string) => {
    getValuesForPolicy(policy).map(field => formik.setFieldTouched(field, true));
  }, MAX_OFFLINE_DEBOUNCE_MS), [formik.setFieldTouched]);

  const handleValueChange = React.useCallback(
    (policy: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
      setDebouncedFieldTouched(policy);
      getValuesForPolicy(policy).map(field => formik.setFieldValue(field, e.target.value));
    },
    // This deps array was noncompliant before we enabled the lint rule. If you're here, please consider fixing it! See TFS 1257824
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [formik.setFieldValue]
  );

  const onDropdownChange = (key: string, value: string) => {
    getSeverityFieldsForPolicy(key).map(field => formik.setFieldValue(field,value));
  };

  const getCurrentSeverityForPolicy = (policy: string) => {
    const severityFields = getSeverityFieldsForPolicy(policy);
    return severityFields ? formik.getFieldProps(severityFields[0]).value : '';
  };

  const getCurrentValueForPolicy = (policy: string) => {
    const values = getValuesForPolicy(policy);
    return values ? formik.getFieldProps(values[0]).value : '';
  };

  const validate = usePositiveIntegerValidator(() => Messages.mobileSecurityPolicies_MaxOffline_NumDaysError());

  const validateMaxDaysOffline = (value: string) => {
    return (value && (/^[1-9][0-9]*$/).test(value)) ? null : Messages.mobileSecurityPolicies_MaxOffline_NumDaysError();
  };

  const getTooltipContent = () => {
    return <span>{Messages.mobileSecurityPolicies_AutoEnabledSetting_TooltipDescription()}</span>;
  };

  return (
    <div data-tb-test-id={MOBILE_SECURITY_SETTINGS_SECTION_TEST_ID}>
      <h2 className={sectionHeader}>{Messages.mobileSecurityPolicies_SectionHeading()}</h2>
      <div className={sectionDescription} data-tb-test-id={MOBILE_SECURITY_SETTINGS_SECTION_DESCRIPTION_TEST_ID}>
         {sessionManagementService.isOnlineEnvironment() ? Messages.mobileSecurityPolicies_SectionDescription_Online() : Messages.mobileSecurityPolicies_SectionDescription_OnPrem()}
         <TextLinkWidget testId={MOBILE_SECURITY_POLICIES_LEARN_MORE_LINK_TEST_ID}
                         target={'_blank'}
                         className={learnMoreStyle}
                         href={HelpService.getServerHelpUrlByCategory('mobile-security-settings')}>
           {Messages.learnMore()}
         </TextLinkWidget>
      </div>

      {/* Jailbreak Detection */}
      <div className={SettingsStyles.indent}>
        <div className={settingSection}>
          <h3 className={SettingsStyles.settingsGroupName}>{Messages.mobileSecurityPolicies_JailBrokenDetection_Heading()}</h3>
          <div className={SettingsStyles.settingsGroupDescription}>{Messages.mobileSecurityPolicies_JailBrokenDetection_Description()}</div>
          <div className={SettingsStyles.checkboxGroup}>
            <FormikCheckbox
              checked={formik.getFieldProps(MOBILE_SECURITY_POLICIES_JAIL_BROKEN_DETECTION_ENABLED_FIELD).value}
              name={MOBILE_SECURITY_POLICIES_JAIL_BROKEN_DETECTION_ENABLED_FIELD}
              label={Messages.mobileSecurityPolicies_JailBrokenDetection_CheckBoxLabel()}
              testId={MOBILE_SECURITY_POLICIES_JAIL_BROKEN_DETECTION_CHECKBOX_TEST_ID}>
              <SeverityLevelDropdown
                 value={getCurrentSeverityForPolicy(JAIL_BROKEN_DETECTION)}
                 policy={JAIL_BROKEN_DETECTION}
                 testId={MOBILE_SECURITY_POLICIES_JAIL_BROKEN_DETECTION_DROPDOWN_TEST_ID}
                 onDropdownChange={onDropdownChange}
              />
            </FormikCheckbox>
          </div>
        </div>
      </div>

      {/* Maximum Days Offline Without Policy Refresh*/}
      <div className={SettingsStyles.indent}>
        <div className={settingSection}>
          <h3 className={SettingsStyles.settingsGroupName}>{Messages.mobileSecurityPolicies_MaxOffline_Heading()}</h3>
          <div className={SettingsStyles.settingsGroupDescription}>{Messages.mobileSecurityPolicies_MaxOffline_Description()}</div>
          <div className={SettingsStyles.checkboxGroup}>
            <FormikCheckbox
              checked={formik.getFieldProps(MOBILE_SECURITY_POLICIES_MAX_OFFLINE_ENABLED_FIELD).value}
              name={MOBILE_SECURITY_POLICIES_MAX_OFFLINE_ENABLED_FIELD}
              label={Messages.mobileSecurityPolicies_MaxOffline_CheckBoxLabel()}
              testId={MOBILE_SECURITY_POLICIES_MAX_OFFLINE_CHECKBOX_TEST_ID}>
                <FormikTextBox
                  style={textFieldStyle}
                  name={MAX_OFFLINE_VALUE}
                  testId={MOBILE_SECURITY_POLICIES_MAX_OFFLINE_TEXT_FIELD_TEST_ID}
                  label={Messages.mobileSecurityPolicies_MaxOffline_NumDaysLabel()}
                  value={formik.values.settings.mobileSecuritySettings.max_offline.iosConfig.value}
                  onChange={handleValueChange(MAX_OFFLINE)}
                  validate={validateMaxDaysOffline}
                  eagerValidation={true}
                />
                <SeverityLevelDropdown
                  value={getCurrentSeverityForPolicy(MAX_OFFLINE)}
                  policy={MAX_OFFLINE}
                  testId={MOBILE_SECURITY_POLICIES_MAX_OFFLINE_DROPDOWN_TEST_ID}
                  onDropdownChange={onDropdownChange}
                />
           </FormikCheckbox>
          </div>
        </div>
      </div>

      {/* Prevent Debugging */}
      <div className={SettingsStyles.indent}>
        <div className={settingSection}>
          <h3 className={SettingsStyles.settingsGroupName}>{Messages.mobileSecurityPolicies_PreventDebugging_Heading()}</h3>
          <div className={SettingsStyles.settingsGroupDescription}>{Messages.mobileSecurityPolicies_PreventDebugging_Description()}</div>
          <div className={SettingsStyles.checkboxGroup}>
            <Tooltip
                  getContent={getTooltipContent}
                  position={TooltipPosition.BottomRightCorner}
                  testId={'prevent-debugging-checkbox-tooltip'}
                  followCursor
              >
                <span>
                  <FormikCheckbox
                    checked
                    name={'antiDebugEnabled'}
                    label={Messages.mobileSecurityPolicies_PreventDebugging_CheckBoxLabel()}
                    testId={MOBILE_SECURITY_POLICIES_ANTI_DEBUG_CHECKBOX_TEST_ID}
                    disabled
                  />
                </span>
              </Tooltip>
          </div>
        </div>
      </div>

      {/* Screen Sharing and Screenshots */}
      <div className={SettingsStyles.indent}>
        <div className={settingSection}>
          <h3 className={SettingsStyles.settingsGroupName}>{Messages.mobileSecurityPolicies_BlockScreenshot_Heading()}</h3>
          <div className={SettingsStyles.settingsGroupDescription}>{Messages.mobileSecurityPolicies_BlockScreenshot_Description()}</div>
          <div className={SettingsStyles.checkboxGroup}>
              <span>
                <FormikCheckbox
                  checked={formik.getFieldProps(MOBILE_SECURITY_POLICIES_BLOCK_SCREENSHOT_ENABLED_FIELD).value}
                  name={MOBILE_SECURITY_POLICIES_BLOCK_SCREENSHOT_ENABLED_FIELD}
                  label={Messages.mobileSecurityPolicies_BlockScreenshot_CheckBoxLabel()}
                  testId={MOBILE_SECURITY_POLICIES_BLOCK_SCREENSHOT_CHECKBOX_TEST_ID}
                />
              </span>
          </div>
        </div>
      </div>
    </div>
  );
}

const textFieldStyle: React.CSSProperties = {
  marginBottom: 6, width: '300px'
};

/* for registering the component in settings registry */
export const MobileSecuritySettingsSectionSpecs = componentRegistryEntry({
  name: 'MobileSecuritySettingsSection',
  component: MobileSecuritySettingsSection
});
